import { h } from 'preact';
import { useEffect, useReducer, useRef, useState } from 'preact/hooks';
import { createEmbroideryMachine } from './machine';
import { makeAddItemsToCart } from '../core/cart/machine/context/add-items-to-cart';
import { sendCartDrawerOpenRequestEvent } from '../core/notifications/cart-drawer-open-request';
import { productEmbroideringData } from './data';
import { generateEmbroideryId } from './utils/generate-embroidery-id';
import { kebabCase } from '../core/string/kebab-case';
import { t } from '../theme/locales';
const closeEmbroideryModal = ()=>{
    try {
        window.dispatchEvent(new CustomEvent('modal:close', {
            detail: {
                id: 'embroidery-modal'
            }
        }));
    } catch (err) {
        console.error('Failed to dispatch modal close event:', err);
    }
};
const refreshCartDrawer = (cart)=>{
    try {
        window.dispatchEvent(new CustomEvent('cart-drawer:update', {
            detail: cart
        }));
    } catch (err) {
        console.error('Failed to dispatch cart update event:', err);
    }
};
const getPixelPositionFromPercentage = (containerWidth, containerHeight, positionXPercentage, positionYPercentage)=>{
    const leftPx = containerWidth * positionXPercentage / 100;
    const topPx = containerHeight * positionYPercentage / 100;
    return {
        leftPx: Number.isFinite(leftPx) ? leftPx : 0,
        topPx: Number.isFinite(topPx) ? topPx : 0
    };
};
const getCoverPixelPositionFromPercentage = (containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight, positionXPercentage, positionYPercentage)=>{
    if (imageNaturalWidth <= 0 || imageNaturalHeight <= 0 || !Number.isFinite(imageNaturalWidth) || !Number.isFinite(imageNaturalHeight)) {
        return getPixelPositionFromPercentage(containerWidth, containerHeight, positionXPercentage, positionYPercentage);
    }
    // object-fit: cover scales by the largest ratio and crops from the center.
    const scale = Math.max(containerWidth / imageNaturalWidth, containerHeight / imageNaturalHeight);
    const renderedWidth = imageNaturalWidth * scale;
    const renderedHeight = imageNaturalHeight * scale;
    const offsetX = (containerWidth - renderedWidth) / 2;
    const offsetY = (containerHeight - renderedHeight) / 2;
    const leftPx = offsetX + renderedWidth * positionXPercentage / 100;
    const topPx = offsetY + renderedHeight * positionYPercentage / 100;
    return {
        leftPx: Number.isFinite(leftPx) ? leftPx : 0,
        topPx: Number.isFinite(topPx) ? topPx : 0
    };
};
const getMonogramError = (text, limit)=>{
    if (text.length === 0) return '';
    if (/[^a-z]/i.test(text)) return t('embroidery.letters_only_error');
    if (text.length > limit) return t('embroidery.max_chars_error').replace('{{ limit }}', limit.toString());
    return '';
};
const getResponsiveMonogramFontSize = (containerWidth, selectedFontStyle)=>{
    const minFontSize = 12;
    const maxFontSize = 64;
    const scaleFactor = 0.03;
    const computed = containerWidth * scaleFactor;
    const baseFontSize = Math.min(maxFontSize, Math.max(minFontSize, computed));
    // Quimby must always render 36.36% larger than other fonts.
    if (selectedFontStyle.includes('Quimby')) {
        return baseFontSize * 1.3636;
    }
    // Dreaming Out Loud Slanted must always render 11.76% larger than other fonts.
    if (selectedFontStyle.includes('Dreaming Out Loud Slanted')) {
        return baseFontSize * 1.1176;
    }
    return baseFontSize;
};
export const Content = ({ targetEl , selectedVariantId , variantTitle , selectedVariantCompareAtPrice , embroideryDisclaimer  })=>{
    var _context_embroideryData, _context_embroideryData1, _context_placements_context_indexPlacement, _context_placements_context_indexPlacement_options_context_indexOption, _context_placements_context_indexPlacement1, _context_placements_context_indexPlacement_options_context_indexOption_font_options, _context_selectedColor, _context_selectedColor1, _context_embroideryData2, _context_embroideryData3, _context_embroideryData4, _context_embroideryData5, _context_embroideryData6, _context_embroideryData7;
    void targetEl;
    const [cta, setCta] = useState(t('embroidery.add_to_bag'));
    const [monogramInputError, setMonogramInputError] = useState('');
    const [pixelPosition, setPixelPosition] = useState({
        leftPx: 0,
        topPx: 0
    });
    const [monogramFontSize, setMonogramFontSize] = useState(16);
    const imageBaseRef = useRef(null);
    const placementRef = useRef(null);
    const [machine] = useState(()=>{
        const freshData = productEmbroideringData(selectedVariantId, variantTitle, selectedVariantCompareAtPrice);
        return createEmbroideryMachine(freshData);
    });
    const [state, dispatch] = useReducer((currentState, event)=>{
        const nextState = machine.transition(currentState, event);
        return nextState;
    }, machine.initialState);
    const send = (event)=>dispatch(event);
    const context = state.context;
    const maxChars = context.limit;
    const remainingChars = maxChars - context.monogramText.length;
    if (context.placements.length === 1 && context.currentStep === 0) {
        var _context_placements_;
        send({
            type: 'SET_PLACEMENT',
            value: context.placements && ((_context_placements_ = context.placements[0]) === null || _context_placements_ === void 0 ? void 0 : _context_placements_.label) ? context.placements[0].label : '',
            index: 0
        });
    }
    const handleMonogramInput = (e)=>{
        const rawValue = e.target.value;
        let sanitizedValue = rawValue.replace(/[^a-z]/gi, '');
        send({
            type: 'SET_MONOGRAM_TEXT',
            value: sanitizedValue
        });
    };
    useEffect(()=>{
        const monogramError = getMonogramError(context.monogramText, context.limit);
        setMonogramInputError(monogramError);
        const monogramValid = context.monogramText.length > 0 && context.monogramText.length <= context.limit && monogramError === '';
        const requiredFieldsFilled = context.selectedPlacement !== '' && context.selectedFontCategory !== '' && context.selectedFontStyle !== '' && context.selectedColor != null;
        const isNextFormValid = monogramValid && requiredFieldsFilled;
        if (context.isFormValid !== isNextFormValid) {
            send({
                type: 'TOGGLE_FORM_VALIDITY',
                value: isNextFormValid
            });
        }
    }, [
        context.monogramText,
        context.limit,
        context.selectedPlacement,
        context.selectedFontCategory,
        context.selectedFontStyle,
        context.selectedColor,
        context.isFormValid
    ]);
    useEffect(()=>{
        var _imageBaseRef_current;
        const updatePixelPosition = ()=>{
            const baseElement = imageBaseRef.current;
            const placementElement = placementRef.current;
            if (!baseElement) {
                return;
            }
            const positionXPercentage = context.positionX;
            const positionYPercentage = context.positionY;
            setMonogramFontSize(getResponsiveMonogramFontSize(baseElement.clientWidth, context.selectedFontStyle));
            const activeImageElement = baseElement.querySelector('picture.active img');
            if (!placementElement) {
                return;
            }
            var _activeImageElement_naturalWidth, _activeImageElement_naturalHeight;
            const { leftPx: centerLeftPx , topPx: centerTopPx  } = getCoverPixelPositionFromPercentage(baseElement.clientWidth, baseElement.clientHeight, (_activeImageElement_naturalWidth = activeImageElement === null || activeImageElement === void 0 ? void 0 : activeImageElement.naturalWidth) !== null && _activeImageElement_naturalWidth !== void 0 ? _activeImageElement_naturalWidth : 0, (_activeImageElement_naturalHeight = activeImageElement === null || activeImageElement === void 0 ? void 0 : activeImageElement.naturalHeight) !== null && _activeImageElement_naturalHeight !== void 0 ? _activeImageElement_naturalHeight : 0, positionXPercentage, positionYPercentage);
            let leftPx = 0;
            let topPx = 0;
            if (context.alignment === 'center') {
                leftPx = centerLeftPx - placementElement.clientWidth / 2;
                topPx = centerTopPx - placementElement.clientHeight / 2;
            } else if (context.alignment === 'left') {
                leftPx = centerLeftPx;
                topPx = centerTopPx - placementElement.clientHeight / 2;
            } else if (context.alignment === 'right') {
                leftPx = centerLeftPx - placementElement.clientWidth;
                topPx = centerTopPx - placementElement.clientHeight / 2;
            }
            setPixelPosition({
                leftPx: Number.isFinite(leftPx) ? leftPx : 0,
                topPx: Number.isFinite(topPx) ? topPx : 0
            });
        };
        updatePixelPosition();
        const resizeObserver = new ResizeObserver(updatePixelPosition);
        if (imageBaseRef.current) {
            resizeObserver.observe(imageBaseRef.current);
        }
        if (placementRef.current) {
            resizeObserver.observe(placementRef.current);
        }
        const activeImageElement = (_imageBaseRef_current = imageBaseRef.current) === null || _imageBaseRef_current === void 0 ? void 0 : _imageBaseRef_current.querySelector('picture.active img');
        if (activeImageElement && !activeImageElement.complete) {
            activeImageElement.addEventListener('load', updatePixelPosition);
        }
        return ()=>{
            if (activeImageElement) {
                activeImageElement.removeEventListener('load', updatePixelPosition);
            }
            resizeObserver.disconnect();
        };
    }, [
        context.indexPlacement,
        context.placements,
        context.monogramText,
        context.selectedFontStyle,
        context.positionX,
        context.positionY,
        context.rotation,
        context.alignment
    ]);
    const handleAddToCart = async ()=>{
        setCta(t('embroidery.adding'));
        if (context.embroideryData) {
            try {
                const parentId = generateEmbroideryId();
                const items = {
                    items: [
                        {
                            id: parseInt(context.embroideryData.current_variant.id),
                            quantity: 1,
                            properties: {
                                _embroidery_id: parentId,
                                _LPROP: 'Final Sale'
                            }
                        },
                        {
                            id: parseInt(context.embroideryData.embroidery_product.id),
                            quantity: 1,
                            parent_id: parseInt(context.embroideryData.current_variant.id),
                            properties: {
                                _embroidery_parent_id: parentId,
                                _embroidery_color: context.hexColor,
                                _font_size: context.selectedFontSize,
                                _LPROP: 'Final Sale',
                                Placement: context.selectedPlacement,
                                Category: context.selectedFontCategory,
                                Font: context.selectedFontStyle,
                                Text: context.monogramText,
                                Color: context.selectedColor
                            }
                        }
                    ]
                };
                const addItemsToCart = makeAddItemsToCart();
                await addItemsToCart(items);
                const updatedCart = await (await fetch('/cart.js')).json();
                closeEmbroideryModal();
                refreshCartDrawer(updatedCart);
                sendCartDrawerOpenRequestEvent();
                setCta(t('embroidery.added'));
            } catch (error) {
                console.error('Failed to add items to cart:', error);
            }
        }
    };
    return h('div', {
        className: 'embroidery'
    }, h('div', {
        className: 'embroidery__image'
    }, [
        h('div', {
            className: 'embroidery__image__base',
            ref: imageBaseRef
        }, [
            ((_context_embroideryData = context.embroideryData) === null || _context_embroideryData === void 0 ? void 0 : _context_embroideryData.images) && ((_context_embroideryData1 = context.embroideryData) === null || _context_embroideryData1 === void 0 ? void 0 : _context_embroideryData1.images.map((image, index)=>h('picture', {
                    className: context.currentStep === 0 && index === 0 ? 'active' : context.currentStep > 0 && index === context.indexPlacement + 1 ? 'active' : ''
                }, h('img', {
                    src: image.src,
                    alt: t('embroidery.embroidery_image_alt'),
                    width: '1024',
                    height: '1024'
                })))),
            context.monogramText.length > 0 && h('div', {
                ref: placementRef,
                className: 'embroidery__image__placement',
                style: `transform: rotate(${context.rotation}deg);` + `left: ${pixelPosition.leftPx}px;` + `top: ${pixelPosition.topPx}px;`
            }, h('p', {
                style: `color: ${context.hexColor || '#000000'};` + `font-size: ${monogramFontSize}px;` + `line-height: ${monogramFontSize}px;`,
                className: 'embroidery__monogram-text ' + kebabCase(context.selectedFontStyle),
                'aria-hidden': 'true'
            }, context.monogramText))
        ])
    ]), h('div', {
        className: 'embroidery__content'
    }, h('div', {
        className: 'embroidery__content__inner'
    }, h('h6', {
        className: 'embroidery__title'
    }, t('embroidery.add_personalization')), h('section', {
        className: `embroidery__section${context.currentStep < 0 ? ' disabled' : ''}`
    }, h('div', {
        className: 'embroidery__row embroidery__row--space-bottom'
    }, h('div', {
        className: 'embroidery__flex-grow'
    }, h('p', {
        className: 'embroidery__section-title'
    }, context.placements.length > 1 ? t('embroidery.choose_placement') : t('embroidery.placement')))), h('div', {
        className: 'embroidery__row embroidery__row--gap'
    }, context.placements.map((placement, index)=>h('label', {
            htmlFor: placement.label,
            className: 'embroidery__radio button'
        }, placement.label, h('input', {
            type: 'radio',
            name: 'placement',
            id: placement.label,
            value: placement.label,
            checked: context.selectedPlacement === placement.label,
            onChange: (e)=>{
                send({
                    type: 'SET_PLACEMENT',
                    value: e.target.value,
                    index: index
                });
            }
        }))))), h('section', {
        className: `embroidery__section${context.currentStep < 1 ? ' disabled' : ''}`,
        'aria-disabled': context.currentStep < 1 ? 'true' : 'false'
    }, h('div', {
        className: 'embroidery__row'
    }, h('details', {
        open: context.currentStep >= 1,
        className: 'embroidery__flex-grow embroidery__details'
    }, h('summary', {
        className: 'embroidery__section-title flex'
    }, h('p', {}, t('embroidery.what_add')), h('svg', {
        width: '24',
        height: '24',
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        'aria-hidden': 'true'
    }, h('path', {
        d: 'M6 9l6 6 6-6',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
    }))), h('div', {
        className: 'embroidery__row embroidery__row--gap embroidery__row--space-top'
    }, (_context_placements_context_indexPlacement = context.placements[context.indexPlacement]) === null || _context_placements_context_indexPlacement === void 0 ? void 0 : _context_placements_context_indexPlacement.options.map((option, index)=>h('label', {
            htmlFor: option.name.toLowerCase(),
            className: 'embroidery__radio button'
        }, h('span', {
            style: 'display: block;'
        }, option.label.split(' ')[0]), option.label.split(' ')[1], h('input', {
            type: 'radio',
            name: 'font-category',
            id: option.name.toLowerCase(),
            value: option.cart_label,
            checked: context.selectedFontCategory === option.cart_label,
            onChange: (e)=>{
                send({
                    type: 'SET_FONT_CATEGORY',
                    value: e.target.value,
                    index: index,
                    limit: option.limit
                });
                if (context.currentStep === 1) {
                    send({
                        type: 'NEXT_STEP'
                    });
                }
                if (context.selectedFontStyle) {
                    send({
                        type: 'SET_FONT_STYLE',
                        value: ''
                    });
                }
                if (context.monogramText) {
                    send({
                        type: 'SET_MONOGRAM_TEXT',
                        value: ''
                    });
                }
            }
        }))))))), h('section', {
        className: `embroidery__section${context.currentStep < 2 ? ' disabled' : ''}`,
        'aria-disabled': context.currentStep < 2 ? 'true' : 'false'
    }, h('div', {
        className: 'embroidery__row'
    }, h('details', {
        open: context.currentStep >= 2,
        className: 'embroidery__flex-grow embroidery__details'
    }, h('summary', {
        className: 'embroidery__section-title flex'
    }, h('p', {}, t('embroidery.add_personalization')), h('svg', {
        width: '24',
        height: '24',
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        'aria-hidden': 'true'
    }, h('path', {
        d: 'M6 9l6 6 6-6',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
    }))), h('div', {
        className: 'embroidery__column embroidery__column--gap'
    }, h('div', {
        className: 'embroidery__column embroidery__column--gap small-gap embroidery__column--space-top'
    }, h('p', {}, t('embroidery.choose_font')), h('div', {
        className: 'embroidery__column embroidery__column--gap extrasmall-gap'
    }, (_context_placements_context_indexPlacement_options_context_indexOption = (_context_placements_context_indexPlacement1 = context.placements[context.indexPlacement]) === null || _context_placements_context_indexPlacement1 === void 0 ? void 0 : _context_placements_context_indexPlacement1.options[context.indexOption]) === null || _context_placements_context_indexPlacement_options_context_indexOption === void 0 ? void 0 : (_context_placements_context_indexPlacement_options_context_indexOption_font_options = _context_placements_context_indexPlacement_options_context_indexOption.font_options) === null || _context_placements_context_indexPlacement_options_context_indexOption_font_options === void 0 ? void 0 : _context_placements_context_indexPlacement_options_context_indexOption_font_options.map((font)=>h('label', {
            htmlFor: kebabCase(font.cta_label),
            className: 'embroidery__radio button ' + kebabCase(font.cta_label)
        }, font.cta_label, h('input', {
            type: 'radio',
            name: 'font-style',
            id: kebabCase(font.cta_label),
            value: kebabCase(font.cta_label),
            checked: context.selectedFontStyle === font.cta_label,
            onChange: ()=>{
                send({
                    type: 'SET_FONT_STYLE',
                    value: font.cta_label
                });
                send({
                    type: 'SET_FONT_SIZE',
                    value: font.size
                });
            }
        }))))), h('div', {
        className: 'embroidery__column embroidery__column--gap small-gap'
    }, h('p', {}, t('embroidery.add_text_label') + ' ' + context.selectedFontCategory.toLowerCase() + ':'), h('div', {
        className: 'embroidery__column embroidery__column--gap no-gap'
    }, h('input', {
        type: 'text',
        id: 'monogram-input',
        name: 'monogram-input',
        className: monogramInputError ? 'embroidery__input embroidery__input-error' : 'embroidery__input ' + kebabCase(context.selectedFontStyle),
        maxLength: maxChars,
        placeholder: t('embroidery.enter_text'),
        value: context.monogramText,
        autoComplete: 'off',
        'aria-label': t('embroidery.add_monogram'),
        'aria-describedby': 'monogram-helper',
        'aria-invalid': monogramInputError.length > 0 ? 'true' : 'false',
        onChange: handleMonogramInput
    }), monogramInputError ? h('small', {
        id: 'monogram-helper',
        role: 'alert'
    }, monogramInputError) : h('small', {
        id: 'monogram-helper',
        'aria-live': 'polite'
    }, t('embroidery.characters_remaining').replace('{{ remaining }}', remainingChars.toString())))), h('div', {
        className: 'embroidery__column embroidery__column--gap small-gap'
    }, h('p', {}, [
        t('embroidery.color_label'),
        h('strong', {}, ' ' + (context.selectedColor && ((_context_selectedColor = context.selectedColor) === null || _context_selectedColor === void 0 ? void 0 : _context_selectedColor.charAt(0).toUpperCase()) + ((_context_selectedColor1 = context.selectedColor) === null || _context_selectedColor1 === void 0 ? void 0 : _context_selectedColor1.slice(1))))
    ]), h('div', {
        className: 'embroidery__row embroidery__row--radio-inputs'
    }, ((_context_embroideryData2 = context.embroideryData) === null || _context_embroideryData2 === void 0 ? void 0 : _context_embroideryData2.colors) && ((_context_embroideryData3 = context.embroideryData) === null || _context_embroideryData3 === void 0 ? void 0 : _context_embroideryData3.colors.map((color)=>h('input', {
            className: 'embroidery__radio',
            style: `background-color: ${color.presentation_color}`,
            type: 'radio',
            name: 'color',
            id: color.name,
            value: color.name,
            'aria-label': color.name,
            checked: context.selectedColor === color.name,
            onChange: (e)=>send({
                    type: 'SET_COLOR',
                    value: e.target.value,
                    presentation_color: color.presentation_color
                })
        }))))))))), h('section', {
        className: 'embroidery__recap'
    }, h('div', {
        className: 'embroidery__recap-table'
    }, h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text bold'
    }, t('embroidery.personalization_summary'))), h('div', {}, h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, (_context_embroideryData4 = context.embroideryData) === null || _context_embroideryData4 === void 0 ? void 0 : _context_embroideryData4.product.title), h('p', {
        className: 'embroidery__text'
    }, '$ ' + ((_context_embroideryData5 = context.embroideryData) === null || _context_embroideryData5 === void 0 ? void 0 : _context_embroideryData5.current_variant.price))), h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, t('embroidery.size_label') + ' ' + ((_context_embroideryData6 = context.embroideryData) === null || _context_embroideryData6 === void 0 ? void 0 : _context_embroideryData6.current_variant.title)))), h('div', {}, context.selectedPlacement && h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, h('span', {
        className: 'embroidery__text bold'
    }, t('embroidery.placement_label')), ' ' + context.selectedPlacement)), context.selectedFontCategory && h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, h('span', {
        className: 'embroidery__text bold'
    }, t('embroidery.personalization_label')), ' ' + context.selectedFontCategory)), context.selectedFontStyle && h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, h('span', {
        className: 'embroidery__text bold'
    }, t('embroidery.font_label')), ' ' + context.selectedFontStyle)), context.monogramText && h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text ' + kebabCase(context.selectedFontStyle)
    }, h('span', {
        className: 'embroidery__text bold'
    }, t('embroidery.text_label')), ' ' + context.monogramText)), context.monogramText && context.selectedColor && h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text'
    }, h('span', {
        className: 'embroidery__text bold'
    }, t('embroidery.color_label')), ' ' + context.selectedColor))), h('div', {}, h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text bold small'
    }, t('embroidery.personalization')), h('p', {
        className: 'embroidery__text'
    }, '$ ' + ((_context_embroideryData7 = context.embroideryData) === null || _context_embroideryData7 === void 0 ? void 0 : _context_embroideryData7.embroidery_product.price))), h('div', {
        className: 'embroidery__row'
    }, h('p', {
        className: 'embroidery__text bold'
    }, t('embroidery.subtotal')), h('p', {
        className: 'embroidery__text bold'
    }, '$ ' + context.subtotal.toFixed(2))))), h('div', {
        className: 'embroidery__text initial',
        dangerouslySetInnerHTML: {
            __html: embroideryDisclaimer || ''
        }
    }), h('div', {
        className: 'embroidery__footer'
    }, [
        h('button', {
            className: 'embroidery__button embroidery__button-add',
            type: 'button',
            disabled: !context.isFormValid,
            'aria-label': t('embroidery.add_to_bag'),
            'data-testid': 'embroidery-personalization-add-to-cart',
            onClick: handleAddToCart
        }, cta)
    ]))), h('div', {
        className: 'embroidery__footer desktop'
    }, [
        h('button', {
            className: 'embroidery__button embroidery__button-add',
            disabled: !context.isFormValid,
            'aria-label': t('embroidery.add_to_bag'),
            'data-testid': 'embroidery-personalization-add-to-cart',
            onClick: handleAddToCart
        }, cta)
    ])));
};
